import { useTranslation } from 'react-i18next';
import { useRef, useEffect, useState } from 'react';

export default function Environments() {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const isScrollingRef = useRef(false);

  const environments = [
    {
      title: t('environments.env1Title'),
      description: t('environments.env1Desc'),
      icon: 'ri-book-open-line',
      image: 'https://public.readdy.ai/ai/img_res/6588b5eb28d90f71fe7fc8d7c4fbb1b6.jpg',
      features: [
        t('environments.env1Feature1'),
        t('environments.env1Feature2'),
        t('environments.env1Feature3'),
        t('environments.env1Feature4')
      ]
    },
    {
      title: t('environments.env2Title'),
      description: t('environments.env2Desc'),
      icon: 'ri-heart-pulse-line',
      image: 'https://readdy.ai/api/search-image?query=Professional%20cleaning%20worker%20in%20protective%20gear%20sanitizing%20medical%20examination%20room%20with%20examination%20table%20medical%20equipment%20cabinets%20and%20sink%20healthcare%20clinic%20interior%20bright%20sterile%20environment%20medical%20office%20cleaning%20service%20realistic%20photography&width=800&height=600&seq=402&orientation=landscape',
      features: [
        t('environments.env2Feature1'),
        t('environments.env2Feature2'),
        t('environments.env2Feature3'),
        t('environments.env2Feature4')
      ]
    },
    {
      title: t('environments.env3Title'),
      description: t('environments.env3Desc'),
      icon: 'ri-store-3-line',
      image: 'https://readdy.ai/api/search-image?query=Retail%20store%20shopping%20mall%20commercial%20space%20clean%20organized%20display%20areas%20common%20corridors%20bright%20modern%20retail%20environment%20professional%20commercial%20cleaning%20service%20minimalist%20background%20high%20quality%20photography&width=800&height=600&seq=203&orientation=landscape',
      features: [
        t('environments.env3Feature1'),
        t('environments.env3Feature2'),
        t('environments.env3Feature3'),
        t('environments.env3Feature4')
      ]
    },
    {
      title: t('environments.env4Title'),
      description: t('environments.env4Desc'),
      icon: 'ri-building-4-line',
      image: 'https://readdy.ai/api/search-image?query=Corporate%20office%20building%20modern%20workspace%20conference%20room%20administrative%20area%20clean%20professional%20business%20environment%20glass%20windows%20bright%20natural%20lighting%20commercial%20cleaning%20service%20minimalist%20background%20high%20quality%20photography&width=800&height=600&seq=204&orientation=landscape',
      features: [
        t('environments.env4Feature1'),
        t('environments.env4Feature2'),
        t('environments.env4Feature3'),
        t('environments.env4Feature4')
      ]
    },
    {
      title: t('environments.env5Title'),
      description: t('environments.env5Desc'),
      icon: 'ri-shield-star-line',
      image: 'https://readdy.ai/api/search-image?query=Professional%20cleaning%20specialist%20in%20protective%20suit%20and%20equipment%20performing%20detailed%20sanitization%20in%20sensitive%20laboratory%20environment%20with%20specialized%20cleaning%20tools%20precision%20cleaning%20of%20delicate%20equipment%20sterile%20controlled%20environment%20professional%20detailed%20cleaning%20service%20realistic%20photography&width=800&height=600&seq=405&orientation=landscape',
      features: [
        t('environments.env5Feature1'),
        t('environments.env5Feature2'),
        t('environments.env5Feature3'),
        t('environments.env5Feature4')
      ]
    }
  ];

  const totalEnvironments = environments.length;

  // Intersection Observer for fade-in effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.05 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Animate mobile cards when section becomes visible
  useEffect(() => {
    if (isVisible) {
      environments.forEach((_, index) => {
        setTimeout(() => {
          setVisibleCards(prev => [...prev, index]);
        }, index * 150);
      });
    }
  }, [isVisible]);

  const handleManualScroll = (direction: 'left' | 'right') => {
    // Prevent multiple clicks while scrolling
    if (isScrollingRef.current) return;
    
    isScrollingRef.current = true;
    setIsPaused(true);
    
    // Clear existing timeout
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    
    // Resume auto-scroll after 10 seconds
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, 10000);

    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = 288 + 16; // card width + gap
      
      let newIndex = currentIndex;
      if (direction === 'left') {
        newIndex = currentIndex <= 0 ? totalEnvironments - 1 : currentIndex - 1;
      } else {
        newIndex = currentIndex >= totalEnvironments - 1 ? 0 : currentIndex + 1;
      }
      
      setCurrentIndex(newIndex);
      container.scrollTo({ left: newIndex * cardWidth, behavior: 'smooth' });
      
      // Allow next click after scroll animation completes
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 500);
    } else {
      isScrollingRef.current = false;
    }
  };

  // Auto-scroll with infinite loop
  useEffect(() => {
    if (isPaused) return;
    
    const container = scrollContainerRef.current;
    if (!container) return;

    const autoScroll = setInterval(() => {
      const cardWidth = 288 + 16; // card width + gap
      let nextIndex = currentIndex + 1;
      
      if (nextIndex >= totalEnvironments) {
        nextIndex = 0;
      }
      
      setCurrentIndex(nextIndex);
      container.scrollTo({ left: nextIndex * cardWidth, behavior: 'smooth' });
    }, 5000);

    return () => clearInterval(autoScroll);
  }, [isPaused, currentIndex, totalEnvironments]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      id="environments" 
      className={`py-12 md:py-24 bg-white transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className={`text-center max-w-3xl mx-auto mb-10 md:mb-20 transition-all duration-500 delay-100 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <span className="inline-block px-3 py-1 md:px-4 md:py-1.5 bg-[#0F172A]/10 text-[#0F172A] text-xs md:text-sm font-medium rounded-full mb-3 md:mb-4">
            {t('environments.badge')}
          </span>
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-3 md:mb-6">
            {t('environments.title')}
          </h2>
          <p className="text-gray-600 text-sm md:text-lg">
            {t('environments.subtitle')}
          </p>
        </div>

        {/* Mobile: Horizontal scroll carousel with navigation arrows */}
        <div className="md:hidden relative">
          {/* Left Arrow */}
          <button 
            onClick={() => handleManualScroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-white transition-colors"
          >
            <i className="ri-arrow-left-s-line text-lg text-[#0F172A]"></i>
          </button>
          
          {/* Right Arrow */}
          <button 
            onClick={() => handleManualScroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:bg-white transition-colors"
          >
            <i className="ri-arrow-right-s-line text-lg text-[#0F172A]"></i>
          </button>

          <div 
            ref={scrollContainerRef}
            className="overflow-x-auto pb-4 px-6 scrollbar-hide scroll-smooth"
          >
            <div className="flex gap-4" style={{ width: 'max-content' }}>
              {environments.map((env, index) => (
                <div 
                  key={index} 
                  className={`w-72 flex-shrink-0 bg-white rounded-xl overflow-hidden shadow-lg transition-all duration-500 ${
                    visibleCards.includes(index) 
                      ? 'opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-6'
                  }`}
                >
                  <div className="relative h-32 overflow-hidden">
                    <img 
                      src={env.image} 
                      alt={env.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/80 to-transparent"></div>
                    <div className="absolute bottom-3 left-3 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] flex items-center justify-center">
                        <i className={`${env.icon} text-sm text-white`}></i>
                      </div>
                      <h3 className="text-sm font-bold text-white">{env.title}</h3>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-600 text-xs mb-3 line-clamp-2">{env.description}</p>
                    <ul className="space-y-1">
                      {env.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-xs text-gray-700">
                          <div className="w-1 h-1 rounded-full bg-[#3B82F6]"></div>
                          <span className="line-clamp-1">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Desktop: Full cards with staggered animation */}
        <div className="hidden md:block space-y-8">
          {environments.map((env, index) => (
            <div 
              key={index} 
              className={`group bg-gradient-to-br from-gray-50 to-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: isVisible ? `${200 + index * 100}ms` : '0ms' }}
            >
              <div className={`grid md:grid-cols-2 gap-0 ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                <div className={`relative h-80 md:h-auto overflow-hidden ${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                  <img 
                    src={env.image} 
                    alt={env.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] flex items-center justify-center mb-3">
                      <i className={`${env.icon} text-2xl text-white`}></i>
                    </div>
                  </div>
                </div>
                
                <div className={`p-8 md:p-12 flex flex-col justify-center ${index % 2 === 1 ? 'md:col-start-1 md:row-start-1' : ''}`}>
                  <h3 className="text-3xl font-bold text-[#0F172A] mb-4">{env.title}</h3>
                  <p className="text-gray-600 text-base mb-6 leading-relaxed">{env.description}</p>
                  
                  <div className="space-y-3">
                    {env.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#3B82F6]/20 to-[#60A5FA]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-[#3B82F6] text-sm"></i>
                        </div>
                        <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA - Mobile optimized with animation */}
        <div className={`mt-10 md:mt-16 text-center bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl md:rounded-3xl p-6 md:p-14 transition-all duration-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`} style={{ transitionDelay: isVisible ? '700ms' : '0ms' }}>
          <div className="max-w-2xl mx-auto">
            <h3 className="text-xl md:text-3xl font-bold text-white mb-3 md:mb-4">
              {t('environments.ctaTitle')}
            </h3>
            <p className="text-white/80 text-sm md:text-base mb-5 md:mb-8">
              {t('environments.ctaSubtitle')}
            </p>
            <button 
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors px-6 py-3 md:px-8 md:py-3.5 rounded-lg bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] hover:from-[#2563EB] hover:to-[#3B82F6] text-white shadow-lg group cursor-pointer"
            >
              {t('environments.ctaButton')}
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
